import { Waypoint } from '@omniafishing/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gzip } from 'fflate';
import { useDispatch } from 'react-redux';
import { WaypointLike } from '../components/map/map_modals/waypoints_modal/waypoint_import_screen';
import { QueryKeys } from '../constants/query_keys';
import { apiV1 } from '../lib/api';
import { objectToFormData } from '../lib/form';
import { FIVE_MINUTES_IN_MS } from '../lib/time';
import { FlashMessageActions } from '../redux/flash_message';
import { useUser } from './use_user';

interface UseWaypointsParams {
  waypointsFilteredFetchParams?: Parameters<typeof apiV1.userWaypointsFetch>[0];
}

const compressData = async (data: WaypointLike[]): Promise<Blob> => {
  try {
    const jsonString = JSON.stringify(data);

    const compressedData = await new Promise<Blob>((resolve, reject) => {
      gzip(new TextEncoder().encode(jsonString), (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(new Blob([result], { type: 'application/octet-stream' }));
        }
      });
    });
    return compressedData;
  } catch (error) {
    console.error('Compression failed:', error);
  }
};

export const useWaypoints = (params: UseWaypointsParams = {}) => {
  const { waypointsFilteredFetchParams } = params;

  const dispatch = useDispatch();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const waypointsFilteredQuery = useQuery({
    queryKey: QueryKeys.ME.WAYPOINTS.WITH_PARAMS(waypointsFilteredFetchParams),
    queryFn: async () => {
      const res = await apiV1.userWaypointsFetch(waypointsFilteredFetchParams);
      return res.data.data;
    },
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: user != null,
  });
  const { data: waypointsFiltered = [], isLoading: waypointsFilteredLoading } =
    waypointsFilteredQuery;

  const waypointsAllQuery = useQuery({
    queryKey: QueryKeys.ME.WAYPOINTS.BASE,
    queryFn: async () => {
      const res = await apiV1.userWaypointsFetch();
      return res.data.data;
    },
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: user != null,
  });
  const { data: waypointsAll = [], isLoading: waypointsAllLoading } = waypointsAllQuery;

  const createWaypointMutation = useMutation({
    mutationFn: async (createWaypointParams: {
      comments?: string;
      imgFile?: File;
      lat: number;
      lng: number;
      name: string;
      waterbody?: string;
      waypoint_type: Waypoint['waypoint_type'];
    }) => {
      const formData = objectToFormData(createWaypointParams);
      if (createWaypointParams.imgFile) {
        formData.append('img', createWaypointParams.imgFile);
      }
      const res = await apiV1.waypointCreate(formData);

      return res.data.data;
    },
    onSuccess: () => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINTS.BASE });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `Waypoint Added!`,
          subheader: `Your Waypoint has been added.`,
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `We're sorry, there was a problem saving this Waypoint.`,
          subheader: 'Please try again or contact support.',
          type: 'error',
        })
      );
    },
  });

  const updateWaypointMutation = useMutation({
    mutationFn: async (updateWaypointParams: {
      id: number;
      waypointParams: {
        comments?: string;
        imgFile?: File;
        lat: number;
        lng: number;
        name: string;
        remove_img?: boolean;
        waterbody?: string;
        waypoint_type: Waypoint['waypoint_type'];
      };
    }) => {
      const { id, waypointParams } = updateWaypointParams;
      const formData = objectToFormData(waypointParams);
      if (waypointParams.imgFile) {
        formData.append('img', waypointParams.imgFile);
      }
      const res = await apiV1.waypointUpdate(id, formData);

      return res.data.data;
    },
    onSuccess: () => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINTS.BASE });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `Waypoint Updated!`,
          subheader: `Your Waypoint has been updated.`,
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `We're sorry, there was a problem saving this Waypoint.`,
          subheader: 'Please try again or contact support.',
          type: 'error',
        })
      );
    },
  });

  const deleteWaypointMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiV1.waypointDelete(id);
    },
    onSuccess: () => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINTS.BASE });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Waypoint Deleted',
          subheader: 'Your Waypoint has been deleted.',
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Your Waypoint Failed to Delete. Please try again.',
          subheader: ' Please try again. If this issue persists, please contact support',
          type: 'error',
        })
      );
    },
  });

  const importWaypointsMutation = useMutation({
    mutationFn: async (waypointsWithoutWaterbody: WaypointLike[]) => {
      const compressedData = await compressData(waypointsWithoutWaterbody);
      const formData = new FormData();
      formData.append('file', compressedData, 'waypoints.gz');

      await apiV1.waypointBulkCreate(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINTS.BASE });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Waypoints Uploaded',
          subheader: 'Your Waypoints have been uploaded.',
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Waypoint Upload Failed.',
          subheader: `We're sorry but there was an error uploading your waypoints. Please try again.`,
          type: 'error',
        })
      );
    },
  });

  return {
    createWaypoint: createWaypointMutation,
    deleteWaypoint: deleteWaypointMutation,
    importWaypoints: importWaypointsMutation,
    updateWaypoint: updateWaypointMutation,
    waypointsAll,
    waypointsAllLoading,
    waypointsFiltered,
    waypointsFilteredLoading,
  };
};
